import {
  DEFAULT_INTENSITY_CLAMP,
  DEFAULT_METS,
  DEFAULT_TIER_CREDIT,
  bandsDefaults,
  boundsDefaults,
  defaultTrustedSources,
  healthAsParsed,
  hrBandsDefaults,
  levelsDefaults,
} from '../../../lib/health/defaults';
import {
  ACTIVITY_CATEGORIES,
  HEALTH_PLATFORMS,
  type HealthBandsDef,
  type HealthBoundsDef,
  type HealthHrBandsDef,
  type HealthLevelsDef,
  type HealthRulesDef,
  type HealthTrustedSourceDef,
} from '../../../lib/health/types';
import {
  CheckboxField,
  Fieldset,
  IssueList,
  NumberField,
  NumberInput,
  SelectInput,
  TextField,
  inputCls,
  smallBtn,
} from '../fields';
import type { FormProps } from '../types';
import { FixedNumbersField } from '../wind/WindForms';
import { HEALTH_LEVEL_NAMES, healthWindDefaults } from '../../../lib/wind/defaults';

const PLATFORM_OPTIONS = HEALTH_PLATFORMS.map((p) => ({
  value: p,
  label: p === 'ios' ? 'ios (Apple Health)' : 'android (Health Connect)',
}));
const TIER_OPTIONS = [0, 1, 2, 3].map((t) => ({ value: String(t), label: `Tier ${t}` }));

/**
 * `healthRules` (shared-cs `HealthRulesDef`, health intake CONTRACT §7): the switches, sync window, daily caps, tier credit, intensity
 * (METs, HR bands, clamp), plausibility bounds, day bands, weekly levels and the trusted sources. An empty tier credit, clamp, METs
 * or trusted-sources list means "the C# defaults" on the server; the form shows those values and fills the list in on the first edit
 * (a list with one row would otherwise replace all thirteen default sources). Issues come from `lib/validate/health.ts`.
 */
export function HealthRulesForm({ value, onChange, errors }: FormProps<HealthRulesDef>) {
  const h = healthAsParsed(value ?? {})!;
  const set = (patch: Partial<HealthRulesDef>) => onChange({ ...h, ...patch });

  const num = (
    k: keyof HealthRulesDef,
    label: string,
    hint: string,
    opts: { integer?: boolean; min?: number; max?: number; step?: number } = {},
  ) => (
    <NumberField
      label={label}
      hint={hint}
      {...opts}
      value={h[k] as number | undefined}
      issues={errors[k]}
      onChange={(v) => set({ [k]: v ?? 0 })}
    />
  );
  const blockNum = <B extends object>(
    blockKey: 'hrBands' | 'bounds' | 'bands' | 'levels',
    defaults: () => B,
    k: keyof B & string,
    label: string,
    hint: string,
    integer = false,
  ) => {
    const b = (h[blockKey] ?? defaults()) as B;
    return (
      <NumberField
        label={label}
        hint={hint}
        integer={integer}
        min={0}
        value={b[k] as number | undefined}
        issues={errors[`${blockKey}.${k}`]}
        onChange={(v) => set({ [blockKey]: { ...b, [k]: v ?? 0 } })}
      />
    );
  };

  const windD = healthWindDefaults().levelWind;
  const levelWind = {
    recovery: h.levelWind?.recovery?.length ? h.levelWind.recovery : windD.recovery,
    rest: h.levelWind?.rest?.length ? h.levelWind.rest : windD.rest,
  };
  const tierCredit = h.tierCredit?.length ? h.tierCredit : null;
  const clamp = h.intensityClamp?.length ? h.intensityClamp : null;
  const mets = h.mets ?? {};
  const sources = h.trustedSources?.length ? h.trustedSources : null;
  const setSources = (next: (HealthTrustedSourceDef | null)[]) => set({ trustedSources: next });
  const editableSources = (): (HealthTrustedSourceDef | null)[] =>
    sources ? [...sources] : defaultTrustedSources();

  return (
    <>
      <Fieldset legend="Switches and sync">
        <CheckboxField
          label="Enabled"
          hint="Off: the phone offers no Connect and the API accepts no syncs."
          checked={h.enabled ?? true}
          onChange={(v) => set({ enabled: v })}
        />
        <CheckboxField
          label="Read dailies (stub)"
          hint="Steps and exercise minutes. Off: the phone asks for neither and sends no dailies."
          checked={h.readDailies ?? false}
          onChange={(v) => set({ readDailies: v })}
        />
        <TextField
          label="Power family"
          mono
          hint="The effect family the phone shows as POWERED UP (aura, sticker, toast) and whose expiry /health/status reports. Seed: powered_up."
          value={h.powerFamily ?? ''}
          issues={errors.powerFamily}
          onChange={(v) => set({ powerFamily: v })}
        />
        {num('importDays', 'Import days', 'How far back the first sync reads.', {
          integer: true,
          min: 1,
        })}
        {num(
          'syncIntervalMin',
          'Sync interval (min)',
          'Minimum minutes between foreground syncs.',
          {
            integer: true,
            min: 1,
          },
        )}
      </Fieldset>

      <Fieldset legend="Daily caps and time limits">
        {num('maxWorkoutsPerDay', 'Max workouts per day', 'Credited rows a day counts.', {
          integer: true,
          min: 1,
        })}
        {num('maxEffortMinPerDay', 'Max effort min per day', 'Effort minutes a day counts.', {
          min: 0,
        })}
        {num(
          'maxWorkoutSec',
          'Max workout (s)',
          'Longer workouts are capped and flagged tooLong.',
          {
            integer: true,
            min: 1,
          },
        )}
        {num('maxDayWorkoutMin', 'Max workout min per day', 'Active minutes a day can hold.', {
          min: 0,
        })}
        {num('lateDays', 'Late after (days)', 'Older workouts are stored with no effort (late).', {
          integer: true,
          min: 1,
        })}
        {num('futureSlackSec', 'Future slack (s)', 'A start this far past now is still accepted.', {
          integer: true,
          min: 0,
        })}
      </Fieldset>

      <Fieldset legend="Tier credit">
        <div className="sm:col-span-2">
          <p className="text-xs text-bruise">
            Share of effort minutes credited per tier (0 manual or failed checks … 3 a trusted
            wearable with heart rate).
            {tierCredit ? '' : ' Using the defaults; edit any value to set your own.'}
          </p>
          <div className="mt-1 grid grid-cols-4 gap-2">
            {[0, 1, 2, 3].map((t) => (
              <label key={t} className="text-xs font-semibold">
                Tier {t}
                <NumberInput
                  label={`Tier ${t} credit`}
                  min={0}
                  max={1}
                  step={0.05}
                  value={(tierCredit ?? DEFAULT_TIER_CREDIT)[t]}
                  onChange={(v) => {
                    const next = [...(tierCredit ?? DEFAULT_TIER_CREDIT)];
                    next[t] = v ?? 0;
                    set({ tierCredit: next });
                  }}
                />
              </label>
            ))}
          </div>
          {tierCredit && (
            <button
              type="button"
              className={`${smallBtn} mt-1`}
              onClick={() => set({ tierCredit: [] })}
            >
              Back to the defaults
            </button>
          )}
          <IssueList issues={errors.tierCredit} />
        </div>
      </Fieldset>

      <Fieldset legend="Intensity">
        <div className="sm:col-span-2">
          <p className="text-xs text-bruise">
            METs per category, used when a workout has no heart rate (intensity = MET / divisor,
            clamped). Blank = the default.
          </p>
          <div className="mt-1 grid grid-cols-3 gap-2">
            {ACTIVITY_CATEGORIES.map((c) => (
              <label key={c} className="text-xs font-semibold">
                {c}
                <NumberInput
                  label={`METs ${c}`}
                  nullable
                  min={0}
                  step={0.5}
                  placeholder={String(DEFAULT_METS[c])}
                  value={mets[c] ?? null}
                  onChange={(v) => {
                    const next = { ...mets };
                    if (v == null) delete next[c];
                    else next[c] = v;
                    set({ mets: next });
                  }}
                />
                <IssueList issues={errors[`mets.${c}`]} />
              </label>
            ))}
          </div>
          {Object.keys(mets)
            .filter((k) => !(ACTIVITY_CATEGORIES as readonly string[]).includes(k))
            .map((k) => (
              <IssueList key={k} issues={errors[`mets.${k}`]} />
            ))}
        </div>
        {num('metIntensityDivisor', 'MET intensity divisor', 'Intensity = MET / this.', { min: 0 })}
        <div>
          <span className="block text-xs font-semibold uppercase tracking-wide">
            Intensity clamp [min, max]
          </span>
          <div className="flex gap-2">
            {[0, 1].map((i) => (
              <NumberInput
                key={i}
                label={i === 0 ? 'Intensity min' : 'Intensity max'}
                min={0}
                step={0.1}
                value={(clamp ?? DEFAULT_INTENSITY_CLAMP)[i]}
                onChange={(v) => {
                  const next = [...(clamp ?? DEFAULT_INTENSITY_CLAMP)];
                  next[i] = v ?? 0;
                  set({ intensityClamp: next });
                }}
              />
            ))}
          </div>
          {!clamp && <p className="mt-0.5 text-xs text-bruise">Using the defaults.</p>}
          <IssueList issues={errors.intensityClamp} />
        </div>
      </Fieldset>

      <Fieldset legend="Heart-rate bands (fraction of max HR)">
        {blockNum<HealthHrBandsDef>(
          'hrBands',
          hrBandsDefaults,
          'easyBelow',
          'Easy below',
          'Under this: easy.',
        )}
        {blockNum<HealthHrBandsDef>(
          'hrBands',
          hrBandsDefaults,
          'hardAbove',
          'Hard above',
          'Over this: hard.',
        )}
        {blockNum<HealthHrBandsDef>(
          'hrBands',
          hrBandsDefaults,
          'easyMult',
          'Easy x',
          'Intensity when easy.',
        )}
        {blockNum<HealthHrBandsDef>(
          'hrBands',
          hrBandsDefaults,
          'moderateMult',
          'Moderate x',
          'Intensity in between.',
        )}
        {blockNum<HealthHrBandsDef>(
          'hrBands',
          hrBandsDefaults,
          'hardMult',
          'Hard x',
          'Intensity when hard.',
        )}
        <IssueList issues={errors.hrBands} />
      </Fieldset>

      <Fieldset legend="Plausibility bounds">
        {blockNum<HealthBoundsDef>(
          'bounds',
          boundsDefaults,
          'runMaxKmh',
          'Run max km/h',
          'Faster: runTooFast.',
        )}
        {blockNum<HealthBoundsDef>(
          'bounds',
          boundsDefaults,
          'walkMaxKmh',
          'Walk max km/h',
          'Faster: counted as a run.',
        )}
        {blockNum<HealthBoundsDef>(
          'bounds',
          boundsDefaults,
          'cycleMaxKmh',
          'Cycle max km/h',
          'Faster: cycleTooFast.',
        )}
        {blockNum<HealthBoundsDef>(
          'bounds',
          boundsDefaults,
          'hrOverMaxBpm',
          'HR over max (bpm)',
          'Average HR this far over the age max: hrOverMax.',
        )}
        {blockNum<HealthBoundsDef>(
          'bounds',
          boundsDefaults,
          'hrFlatBpm',
          'HR flat (bpm)',
          'Max − avg under this: hrFlat.',
        )}
        {blockNum<HealthBoundsDef>(
          'bounds',
          boundsDefaults,
          'hrFlatMinSamples',
          'HR flat min samples',
          'hrFlat only with this many samples.',
          true,
        )}
        {blockNum<HealthBoundsDef>(
          'bounds',
          boundsDefaults,
          'kcalPerMinMax',
          'Max kcal/min',
          'More: kcal ignored.',
        )}
        {blockNum<HealthBoundsDef>(
          'bounds',
          boundsDefaults,
          'defaultMaxHr',
          'Default max HR',
          'When the age is unknown.',
        )}
        {num(
          'overlapFraction',
          'Overlap fraction',
          'Same-category windows overlapping more than this are one workout.',
          {
            min: 0,
            max: 1,
            step: 0.05,
          },
        )}
        {num(
          'hrCoverageForTier3',
          'HR coverage for tier 3',
          'Share of active minutes with heart rate.',
          {
            min: 0,
            max: 1,
            step: 0.05,
          },
        )}
        <IssueList issues={errors.bounds} />
      </Fieldset>

      <Fieldset legend="Day bands (credited min per day)">
        {blockNum<HealthBandsDef>('bands', bandsDefaults, 'light', 'Light', 'At least.')}
        {blockNum<HealthBandsDef>('bands', bandsDefaults, 'moderate', 'Moderate', 'At least.')}
        {blockNum<HealthBandsDef>('bands', bandsDefaults, 'vigorous', 'Vigorous', 'At least.')}
        <IssueList issues={errors.bands} />
      </Fieldset>

      <Fieldset legend="Levels (credited min over 7 days)">
        {blockNum<HealthLevelsDef>('levels', levelsDefaults, 'walker', 'Walker', 'At least.')}
        {blockNum<HealthLevelsDef>('levels', levelsDefaults, 'mover', 'Mover', 'At least.')}
        {blockNum<HealthLevelsDef>('levels', levelsDefaults, 'athlete', 'Athlete', 'At least.')}
        {blockNum<HealthLevelsDef>('levels', levelsDefaults, 'beast', 'Beast', 'At least.')}
        <IssueList issues={errors.levels} />
      </Fieldset>

      <Fieldset legend="Wind from the level (WIND)">
        {num(
          'levelMinTier',
          'Lowest trust tier that counts',
          '0..3. Workouts below this trust tier do not count toward the wind level.',
          { integer: true, min: 0, max: 3 },
        )}
        <FixedNumbersField
          label="Recovery multiplier by level"
          hint="body.windRecovery by level. Empty in the bundle = these defaults; the first edit writes all five."
          names={HEALTH_LEVEL_NAMES}
          value={levelWind.recovery}
          issues={errors['levelWind.recovery']}
          min={0}
          step={0.05}
          onChange={(v) => set({ levelWind: { ...levelWind, recovery: v } })}
        />
        <FixedNumbersField
          label="Rest multiplier by level"
          hint="body.windRest by level. Empty in the bundle = these defaults; the first edit writes all five."
          names={HEALTH_LEVEL_NAMES}
          value={levelWind.rest}
          issues={errors['levelWind.rest']}
          min={0}
          step={0.05}
          onChange={(v) => set({ levelWind: { ...levelWind, rest: v } })}
        />
      </Fieldset>

      <Fieldset legend="Trusted sources">
        <div className="sm:col-span-2">
          <p className="text-xs text-bruise">
            Apps that write workouts, by bundle or package id (an id matches by prefix, so
            com.apple.health covers com.apple.health.&lt;device&gt;). Tier is the highest tier the
            app's rows can reach. See Live ops → Health sources for the apps players actually use.
            {sources ? '' : ' Using the 13 defaults; any edit copies them in first.'}
          </p>
          <table className="mt-1 w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase">
                <th className="pr-2">Id</th>
                <th className="pr-2">Platform</th>
                <th className="pr-2">Tier</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(sources ?? defaultTrustedSources()).map((s, i) => {
                const update = (patch: Partial<HealthTrustedSourceDef>) => {
                  const next = editableSources();
                  next[i] = { ...(next[i] ?? { id: '', platform: 'ios', tier: 0 }), ...patch };
                  setSources(next);
                };
                return (
                  <tr key={i} className="align-top">
                    <td className="pr-2">
                      <input
                        aria-label={`Source ${i + 1} id`}
                        className={`${inputCls} font-mono`}
                        value={s?.id ?? ''}
                        onChange={(e) => update({ id: e.target.value })}
                      />
                      <IssueList
                        issues={[
                          ...(errors[`trustedSources.${i}.id`] ?? []),
                          ...(errors[`trustedSources.${i}`] ?? []),
                        ]}
                      />
                    </td>
                    <td className="pr-2">
                      <SelectInput
                        label={`Source ${i + 1} platform`}
                        options={PLATFORM_OPTIONS}
                        value={s?.platform}
                        onChange={(v) => update({ platform: v ?? '' })}
                      />
                      <IssueList issues={errors[`trustedSources.${i}.platform`]} />
                    </td>
                    <td className="pr-2">
                      <SelectInput
                        label={`Source ${i + 1} tier`}
                        options={TIER_OPTIONS}
                        value={String(s?.tier ?? 0)}
                        onChange={(v) => update({ tier: Number(v ?? 0) })}
                      />
                      <IssueList issues={errors[`trustedSources.${i}.tier`]} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`${smallBtn} text-blam`}
                        aria-label={`Remove source ${i + 1}`}
                        onClick={() => setSources(editableSources().filter((_, j) => j !== i))}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-1 flex gap-2">
            <button
              type="button"
              className={smallBtn}
              onClick={() =>
                setSources([...editableSources(), { id: '', platform: 'ios', tier: 2 }])
              }
            >
              Add source
            </button>
            {sources && (
              <button type="button" className={smallBtn} onClick={() => setSources([])}>
                Back to the defaults
              </button>
            )}
          </div>
        </div>
      </Fieldset>
    </>
  );
}
